import { FaEraser, FaPlus, FaSearch } from "react-icons/fa";
import Button from "../../../../../components/button/button";
import { Template } from "../../../../../components/template/template";
import { Card } from "../../../../../components/card/card";
import InputForm from "../../../../../components/input/input-form";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../../../utils/aggrid-utils";
import { useMemo, useRef, useState } from "react";
import { navigateHook } from "../../../../../hooks/use-navigate";
import { useQuery } from "@tanstack/react-query";
import NotificationVisibilityService from "../../../../../services/web/notification-visibility-service";

export default function NotificationsVisibility() {
    const navigate = navigateHook();

    const [origin, setOrigin] = useState("");
    const [originInput, setOriginInput] = useState("");

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ["notificationVisibility"],
        queryFn: async () => {
            const payload = { filter: [], page: 1, size: 999999, sort: [{ field: "origin", dir: "asc" }] };
            const root = await NotificationVisibilityService.list(payload);
            return root?.data?.data ?? [];
        },
    });

    const filteredRows = useMemo(() => {
        const o = (origin || "").trim().toLowerCase();
        if (!o) return rows;
        return rows.filter(r => (r.origin ?? "").toLowerCase().includes(o));
    }, [rows, origin]);

    const columnDefs = useMemo(() => {
        const originComparator = (a, b) => String(a ?? "").toUpperCase().localeCompare(String(b ?? "").toUpperCase(), "pt-BR");
        
        return [
            {
                headerName: "Origem",
                field: "origin",
                sortable: true,
                comparator: originComparator,
                flex: 1,
            },
            {
                headerName: "Grupos de operação",
                field: "groups_formatted",
                sortable: true,
                comparator: AgGridUtils.nullLastComparator,
                flex: 1,
            },
        ];
    }, []);

    const gridRef = useRef(null);
    const goFirstPage = () => gridRef.current?.api?.paginationGoToFirstPage();

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {handleSearch()};
        if (e.key === "Escape") {handleClear()};
    };

    const handleSearch = () => {
        setOrigin(originInput.trim());
        goFirstPage();
    };

    const handleClear = () => {
        setOrigin("");
        setOriginInput("");
        goFirstPage();
    };

    const onCellClicked = ({ data, event }) => {
        if (!data?.id) return;

        const urlDev = `/notifications-visibility/form/${data.id}`;
        let isBlank = !!event.ctrlKey;

        navigate(urlDev, isBlank);
    };

    const newVisibility = () => {
        const urlDev = "/notifications-visibility/form";
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Visibilidade</Template.Title>
                <Button onClick={newVisibility}><FaPlus />Adicionar visibilidade</Button>
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm
                                id="origin"
                                label="Origem"
                                value={originInput}
                                onChange={(e) => setOriginInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                classNameWrapper="flex-1"
                            />
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-153px)] w-full">
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredRows}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                suppressCellFocus
                                animateRows={false}
                                onCellClicked={onCellClicked}
                                onFilterChanged={AgGridUtils.filterChangedNoRowsOverlay}
                                gridOptions={{localeText: AgGridUtils.localization()}}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}